import { useRouterState } from '@tanstack/react-router';
import {
  BookOpen,
  Building2,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Fuel,
  LayoutDashboard,
  Package,
  Truck,
  Users,
  Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';

import {
  MODULOS_DO_SISTEMA,
  type CaminhoConstruido,
  type IdModulo,
  type ModuloDoSistema,
} from '@/modulos/acesso/sprint';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { ABAS_DO_COMBUSTIVEL } from '@/modulos/combustivel/abas';

import { NavegacaoLateral } from './navegacao/NavegacaoLateral';

/**
 * Navegacao lateral (v2.1, p.34): MENU FLUTUANTE de 248 px, raio 20, afastado
 * 12 px das bordas, com o logotipo e a assinatura da Atenas no topo, os
 * modulos em QUATRO GRUPOS (Visao, Frota, Manutencao, Gestao) e a Biblioteca
 * tecnica fixa no pe, no azul de consulta.
 *
 * O MENU MOSTRA O QUE A PESSOA ABRE: o recorte sai das PERMISSOES da sessao,
 * as mesmas que o servidor confere em cada pedido (ver `modulosVisiveis`). O
 * modulo que ainda nao tem tela aparece desligado, com o motivo no `title`.
 *
 * EM SANFONA (melhoria pedida pelo dono em 24/09). Cada modulo abre as
 * proprias telas logo abaixo dele, como um cardapio: clicar num modulo fechado
 * leva a tela principal dele e mostra as outras; clicar no modulo aberto o
 * recolhe. Um aberto por vez — o que esta aberto e onde voce esta, e dois
 * abertos devolvem a lista comprida que a sanfona existe para evitar. Antes as
 * secoes moravam num grupo "Modulo atual" separado, no pe da barra, longe do
 * item que as abria.
 *
 * Todo modulo CONSTRUIDO tem secao, mesmo quando ela e uma so: um item que
 * abre sanfona em uns e navega direto em outros ensina a pessoa a desconfiar
 * do clique. Modulo sem tela continua desligado, com o motivo no `title`.
 * As secoes nao tem icone: o vocabulario do manual (p.14) nomeia conceitos, e
 * nao telas, e inventar um por tela encheria a barra de simbolo sem norma. O
 * que as liga ao modulo e o recuo e o fio vertical, em cinza.
 *
 * Os icones sao os do vocabulario da v2.1 (p.19), um conceito por icone:
 * Painel e `layout-dashboard`, Cadastros e `building-2`, Pessoas e `users`.
 *
 * Abaixo de 1280 px a navegacao vira TRILHO DE ICONES (72 px), e abaixo de
 * 1024 vira gaveta pelo botao ☰ da barra superior (p.18). O painel de detalhe
 * quebra no mesmo 1280, onde vira gaveta.
 *
 * NO TRILHO DE ICONES (1024-1279) AS SECOES CONTINUAM ALCANCAVEIS. Sem texto,
 * a sanfona nao cabe, e as segundas telas dos modulos (Cadastrar equipamento,
 * Visao do gestor, Importacoes, Auditoria...) so se alcancavam por link
 * interno, quando havia. Ali o clique num modulo com secoes abre um MENU
 * ANCORADO ao icone (p.29: colado ao gatilho, a 8 px), com as secoes.
 *
 * A GAVETA (abaixo de 1024) tem ✕ no topo, primeiro na ordem do foco (p.29:
 * "Esc e ✕ fecham"), e no pe o que saiu da barra superior para ela caber em
 * tela estreita: o tema, a pessoa (abaixo de 768), "Sair do sistema" e a
 * assinatura da Atenas (abaixo de 768; a "assinatura vertical" da p.24 e a do
 * rodape da navegacao).
 */

interface SecaoNav {
  readonly rotulo: string;
  readonly para: CaminhoConstruido;
  /**
   * A licença que a tela pede, quando a tela INTEIRA é uma ação (cadastrar,
   * montar): sem ela a seção não aparece (prova do marco, 07/10/2026 — a
   * Oficina via "Cadastrar equipamento" no menu). A licença de LER a tela não
   * se escreve aqui: ela vem de `LEITURA_DA_TELA`, a mesma que a rota guarda.
   */
  readonly exige?: string;
}

interface ModuloNav extends ModuloDoSistema {
  /** Todo modulo tem icone do vocabulario normativo (p.14). Sem excecao. */
  readonly Icone: ComponentType<{ className?: string }>;
  readonly secoes?: readonly SecaoNav[];
}

/* O MAPA MORA EM `modulos/acesso/sprint.ts`, e nao mais aqui.
   Ele era uma constante privada deste arquivo ate a tela de login precisar da
   MESMA lista para dizer quais modulos cada perfil ve. Duas copias divergiriam
   na primeira vez que um modulo nascesse, e a divergencia apareceria como "o
   login promete um modulo que o menu nao tem".

   O que ficou aqui e o que so a navegacao sabe: o icone e as secoes. Os dois
   `Record<IdModulo, ...>` abaixo cobram exaustividade — modulo novo no mapa sem
   icone nesta tabela e erro de compilacao, e nao um espaco em branco no menu. */
const ICONES: Record<IdModulo, ComponentType<{ className?: string }>> = {
  equipamentos: Truck,
  combustivel: Fuel,
  manutencao: Wrench,
  ordens: ClipboardList,
  horimetro: Clock,
  obras: Building2,
  operadores: Users,
  pecas: Package,
  custos: CircleDollarSign,
  relatorios: LayoutDashboard,
  biblioteca: BookOpen,
};

/* OS QUATRO GRUPOS DO MENU (p.34). A Biblioteca tecnica nao entra em grupo:
   ela e o cartao fixo do pe. O `Record` cobra que todo modulo do mapa tenha
   grupo — modulo novo sem lugar e erro de compilacao. */
type GrupoDoMenu = 'Visão' | 'Frota' | 'Manutenção' | 'Gestão';
const GRUPO_DO_MODULO: Record<Exclude<IdModulo, 'biblioteca'>, GrupoDoMenu> = {
  relatorios: 'Visão',
  equipamentos: 'Frota',
  combustivel: 'Frota',
  horimetro: 'Frota',
  manutencao: 'Manutenção',
  ordens: 'Manutenção',
  pecas: 'Manutenção',
  custos: 'Gestão',
  operadores: 'Gestão',
  obras: 'Gestão',
};
/* A ORDEM DOS GRUPOS saiu com a sanfona (26/10/2026): o trilho do desenho é
   uma lista única de módulos, e o agrupamento passou para DENTRO do painel,
   por tela ("Dia a dia", "Controle", "Campo"). O que sobreviveu é o
   `GRUPO_DO_MODULO`, que não era da sanfona: é ele que a TRILHA da barra
   superior escreve — "Frota › Combustível › Lançamentos". */
/* A ordem dentro de cada grupo e a da p.34. */
const ORDEM_DOS_MODULOS: readonly Exclude<IdModulo, 'biblioteca'>[] = [
  'relatorios',
  'equipamentos',
  'combustivel',
  'horimetro',
  'manutencao',
  'ordens',
  'pecas',
  'custos',
  'operadores',
  'obras',
];

const SECOES: Partial<Record<IdModulo, readonly SecaoNav[]>> = {
  /* Os modulos de uma tela so tambem tem secao (ver o cabecalho): o rotulo e o
     titulo da propria pagina, para o menu e a tela dizerem a mesma coisa. */
  manutencao: [
    { rotulo: 'Planos e vencimentos', para: '/manutencao' },
    /* 0105: a manutencao preditiva (modulo 18) mora AQUI, sob a Manutencao:
       e o historico das corretivas e o que ele permite prever, e o
       vocabulario de icones (p.19) nao tem conceito para preditiva. So para
       quem le a preditiva: o Planejador de manutencao e a Oficina, na proposta. */
    { rotulo: 'Preditiva · histórico de falhas', para: '/manutencao/preditiva', exige: 'preditiva.ler' },
    { rotulo: 'Preditiva · recorrências', para: '/manutencao/preditiva/recorrencias', exige: 'preditiva.ler' },
    { rotulo: 'Preditiva · previsões em piloto', para: '/manutencao/preditiva/previsoes', exige: 'preditiva.ler' },
    { rotulo: 'Preditiva · propostas técnicas', para: '/manutencao/preditiva/propostas', exige: 'preditiva.ler' },
  ],
  /* Inspecoes (modulo 10) entra AQUI, sob o mesmo `clipboard-list`, pela
     terceira vez da mesma regra: o vocabulario de icones do manual (p.14) nao
     tem entrada para checklist, e inventar uma e decisao de quem assina o
     manual. O reuso e o mais honesto dos tres — a ferramenta 4 do v6 e,
     textualmente, "relaciona ocorrencia, OS e liberacao". */
  operadores: [{ rotulo: 'Impedimentos e documentos', para: '/pessoas' }],
  ordens: [
    { rotulo: 'Fila de O.S.', para: '/ordens' },
    { rotulo: 'Inspeções e ocorrências', para: '/inspecao' },
    /* Montar é a ação: quem só lê a inspeção (o operador, a diretoria) não
       recebe no menu uma tela de montar que não conclui (robôs, 20/10/2026). */
    { rotulo: 'Montar checklists', para: '/inspecao/checklists', exige: 'checklist.gravar' },
  ],
  horimetro: [
    { rotulo: 'Turnos, horas e paradas', para: '/operacao' },
    { rotulo: 'Conciliação de horas', para: '/operacao/conciliacao' },
    { rotulo: 'Importações', para: '/operacao/importacoes' },
    { rotulo: 'Folhas de turno', para: '/operacao/folhas' },
    { rotulo: 'Causas e turno', para: '/operacao/cadastros' },
    /* 0103: a telemetria (modulo 17) mora AQUI, sob a Operacao: e mais uma
       origem das horas e do horimetro, e o vocabulario de icones (p.19) nao
       tem conceito para telemetria. So para quem le a telemetria. */
    { rotulo: 'Telemetria · sinal', para: '/operacao/telemetria', exige: 'telemetria.ler' },
    { rotulo: 'Telemetria · fila', para: '/operacao/telemetria/fila', exige: 'telemetria.ler' },
    { rotulo: 'Telemetria · conciliação', para: '/operacao/telemetria/conciliacao', exige: 'telemetria.ler' },
    { rotulo: 'Telemetria · integrações', para: '/operacao/telemetria/integracoes', exige: 'telemetria.ler' },
  ],
  /* Compras (modulo 09) entra AQUI, sob o mesmo `package`, pela mesma regra
     que pos Receita sob Custos: o vocabulario de icones do manual (p.14) nao
     tem entrada para compras, e inventar uma e decisao de quem assina o
     manual. O reuso e honesto porque a demanda nasce do saldo e o recebimento
     volta para o livro do estoque — as duas pontas da mesma pergunta. */
  pecas: [
    { rotulo: 'Saldos e reservas', para: '/estoque' },
    { rotulo: 'Compras e reposição', para: '/compras' },
  ],
  /* Medicoes e receita (modulo 14) entra AQUI, sob o mesmo icone, e nao como
     modulo proprio: nenhum dos doze conceitos do vocabulario (p.14) nomeia
     receita, e `circle-dollar-sign` ja e dinheiro. Inventar um icone e decisao
     de quem assina o manual, e nao da tela — a mesma regra que deixou a previa
     (modulo 22) fora do menu. O reuso aqui e honesto porque a pergunta e uma
     so: margem e custo contra receita. */
  custos: [
    { rotulo: 'Custo por máquina', para: '/custos' },
    { rotulo: 'Medições e receita', para: '/receita' },
    /* 0102: o financeiro gerencial e a DRE (modulo 15) entram AQUI, sob o
       mesmo icone, pela regra que pos a receita: nenhum conceito do
       vocabulario (p.19) nomeia o financeiro, e a pergunta continua a mesma —
       custo contra receita, agora com o plano de contas. So para quem le o
       financeiro: a Diretoria e a Controladoria, na proposta. */
    { rotulo: 'Financeiro e DRE', para: '/financeiro', exige: 'financeiro.ler' },
    /* 0106: o orcamento e planejamento (modulo 19), pela mesma regra: e o
       plano contra o qual o custo e a receita se leem. So para quem le o
       orcamento: a Diretoria e a Controladoria, na proposta. */
    { rotulo: 'Orçamento e caixa', para: '/orcamento', exige: 'orcamento.ler' },
    /* 0107: o plano de investimentos (modulo 20, ferramenta 4), pela mesma
       regra: e o investimento contra o caixa do orcamento. */
    { rotulo: 'Plano de investimentos', para: '/investimentos', exige: 'renovacao.ler' },
  ],
  relatorios: [
    { rotulo: 'Situação e exceções', para: '/painel' },
    /* Modulo 21 (25/09): a visao do gestor mora sob o Painel, e nao num item
       proprio — o vocabulario de icones do manual nao tem entrada para
       "gestao", e o Painel ja e o bar-chart-3 de relatorio. */
    { rotulo: 'Visão do gestor', para: '/painel/gestor' },
    /* Etapa C: o registro e a qualidade da IA, sob o Painel pelo mesmo motivo
       da visao do gestor — o vocabulario nao tem item proprio para isso. */
    { rotulo: 'Qualidade da IA', para: '/painel/ia' },
    /* 0100: o piloto controlado (guia §17), sob o Painel pelo mesmo motivo
       da visao do gestor — e leitura de direcao, e o vocabulario de icones
       nao tem item proprio para ela. */
    { rotulo: 'Piloto', para: '/painel/piloto' },
  ],
  /* AS TRES TELAS DO MODULO 02. `/equipamentos/novo` e `/equipamentos/catalogo`
     existiam e so eram alcancaveis pelos botoes do cabecalho da frota — o
     modulo que as construiu nao edita este arquivo, que e compartilhado, e
     registrou as duas linhas como pendencia. Sao estas. */
  equipamentos: [
    { rotulo: 'Frota', para: '/equipamentos' },
    { rotulo: 'Cadastrar equipamento', para: '/equipamentos/novo', exige: 'equipamento.gerir' },
    { rotulo: 'Catálogo', para: '/equipamentos/catalogo' },
    /* Decisão 64 (31/10/2026): o arquivo dos adesivos de identificação. */
    { rotulo: 'Identificação', para: '/equipamentos/identificacao' },
    /* 0107: a renovacao da frota (modulo 20) mora AQUI, sob a Frota: e a
       economia de cada maquina, e o vocabulario de icones (p.19) nao tem
       conceito para renovacao. So para quem le a renovacao: a Diretoria, o
       Gestor operacional e a Controladoria, na proposta. */
    { rotulo: 'Renovação', para: '/equipamentos/renovacao', exige: 'renovacao.ler' },
  ],
  /* O modulo 01 do mapa funcional v6 mora no item `obras` desta navegacao — ver
     a nota em `modulos/acesso/sprint.ts`, onde o rotulo virou "Cadastros". As
     tres secoes sao as tres telas dele, e nao tres modulos. */
  obras: [
    { rotulo: 'Estrutura', para: '/cadastros' },
    { rotulo: 'Perfis e responsáveis', para: '/cadastros/governanca' },
    { rotulo: 'Auditoria', para: '/cadastros/trilha' },
    /* 0104: as integracoes com o ERP (modulo 04) moram AQUI, sob Cadastros: a
       matriz de sistema mestre e o de-para sao cadastro da empresa, e o
       vocabulario de icones (p.19) nao tem conceito para integracao. So para
       quem le as integracoes: a Diretoria e a Controladoria, na proposta. */
    { rotulo: 'Integrações · matriz', para: '/cadastros/integracoes', exige: 'integracao.ler' },
    { rotulo: 'Integrações · lotes', para: '/cadastros/integracoes/lotes', exige: 'integracao.ler' },
    { rotulo: 'Integrações · títulos e notas', para: '/cadastros/integracoes/titulos', exige: 'integracao.ler' },
    { rotulo: 'Integrações · correspondências', para: '/cadastros/integracoes/correspondencias', exige: 'integracao.ler' },
    { rotulo: 'Integrações · divergências', para: '/cadastros/integracoes/divergencias', exige: 'integracao.ler' },
  ],
  /* AS NOVE TELAS DO COMBUSTIVEL VOLTARAM AO MENU (pedido do dono,
     25/10/2026: *"Eu nao quero mais abas internas"*).
     
     Em 25/09 elas tinham virado ABAS dentro de um hub, e o menu passou a
     listar uma linha so. O trilho de abas era uma segunda navegacao, com
     outra regra e outro lugar na tela: quem estava em Tanques e queria
     Cobrancas procurava no alto da pagina, e quem estava em outro modulo
     procurava na lateral. Agora e uma navegacao so, e a sanfona do modulo faz
     o que o trilho fazia.
     
     A LISTA SAI DE `combustivel/abas.ts`, a mesma que a pagina usa para
     escrever a explicacao da tela aberta. Copiar os nove rotulos aqui criaria
     duas listas para divergirem na proxima tela nova. */
  combustivel: ABAS_DO_COMBUSTIVEL.map((aba) =>
    /* O formulario inteiro E a acao: quem le o combustivel e nao lanca (a
       Diretoria) nao recebe no menu uma tela que recusa. O mesmo recorte que o
       hub fazia no trilho. */
    aba.para === '/combustivel/lancamento/completo'
      ? { rotulo: aba.rotulo, para: aba.para, exige: 'abastecimento.registrar' }
      : { rotulo: aba.rotulo, para: aba.para },
  ),
  /* O modulo 12 tem UMA tela por enquanto. A secao existe mesmo assim: sem
     ela, o item do menu nao leva a lugar nenhum e parece quebrado. */
  biblioteca: [{ rotulo: 'Manuais e documentos', para: '/tecnico' }],
};

const TODOS: readonly ModuloNav[] = MODULOS_DO_SISTEMA.map((modulo) => {
  const secoes = SECOES[modulo.id];
  return { ...modulo, Icone: ICONES[modulo.id], ...(secoes ? { secoes } : {}) };
});

/**
 * Os módulos NA ORDEM DO MANUAL (p.34), que é a ordem em que o trilho os
 * desenha: Painel primeiro, a Frota, a Manutenção, a Gestão, e a Biblioteca
 * técnica por último, no pé.
 *
 * A ordem de `MODULOS_DO_SISTEMA` é a do mapa de produto, e não a da tela —
 * ela nasceu da sequência de construção. Usar uma pela outra punha o Painel
 * no fim do trilho, onde ninguém o procura.
 *
 * `ORDEM_DOS_GRUPOS` + `ORDEM_DOS_MODULOS` continuam sendo a fonte, e o
 * `Record` exaustivo delas é que garante que módulo novo tenha lugar: sem
 * lugar, não compila — e não some do trilho em silêncio.
 */
export const MODULOS: readonly ModuloNav[] = [
  ...ORDEM_DOS_MODULOS.map((id) => TODOS.find((m) => m.id === id)).filter((m): m is ModuloNav => m !== undefined),
  ...TODOS.filter((m) => m.id === 'biblioteca'),
];


/**
 * ONDE A PESSOA ESTA: o modulo e a tela do caminho. A tela e a secao cujo
 * caminho casa por mais tempo — `/painel/gestor` e a Visao do gestor, e nao o
 * Painel; `/inspecao` e Ordens de servico, e nao modulo nenhum. Serve ao item
 * ativo do menu e a trilha da barra superior.
 */
function localDoCaminho(caminho: string): { readonly modulo: ModuloNav; readonly secao: SecaoNav | null } | null {
  let melhor: { readonly modulo: ModuloNav; readonly secao: SecaoNav | null; readonly tamanho: number } | null = null;
  for (const modulo of MODULOS) {
    const candidatos: readonly (SecaoNav | null)[] = modulo.secoes ?? [null];
    for (const secao of candidatos) {
      const para = secao?.para ?? modulo.para;
      if (para === undefined) continue;
      if (caminho !== para && !caminho.startsWith(`${para}/`)) continue;
      if (melhor === null || para.length > melhor.tamanho) melhor = { modulo, secao, tamanho: para.length };
    }
  }
  return melhor === null ? null : { modulo: melhor.modulo, secao: melhor.secao };
}

/** A trilha da barra superior (p.34): grupo, modulo e, quando o modulo tem varias telas, a tela. */
function trilhaDoCaminho(caminho: string): readonly string[] {
  const local = localDoCaminho(caminho);
  if (local === null) return caminho === '/perfil' ? ['Meu perfil'] : [];
  const { modulo, secao } = local;
  const grupo = modulo.id === 'biblioteca' ? null : GRUPO_DO_MODULO[modulo.id];
  const partes = [...(grupo ? [grupo] : []), modulo.rotulo, ...(secao ? [secao.rotulo] : [])];
  /* O nome repetido sai: "Manutenção › Manutenção" e "Combustível ›
     Combustível" diziam a mesma coisa duas vezes. */
  return partes.filter((parte, indice) => parte !== partes[indice - 1]);
}

/**
 * A TRILHA (p.34), desenhada na barra superior: onde a pessoa esta, do grupo a
 * tela. Mora aqui, ao lado do mapa do menu de onde ela sai, e a casca a entrega
 * a barra — o nome do grupo e do modulo e o mesmo nos dois lugares.
 */
export function Trilha() {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const trilha = trilhaDoCaminho(caminho);
  if (trilha.length === 0) return null;
  return (
    <nav aria-label="Você está em" className="flex min-w-0 items-center gap-2 max-[768px]:hidden">
      <ol className="flex min-w-0 items-center gap-2">
        {trilha.map((parte, indice) => {
          const ultima = indice === trilha.length - 1;
          return (
            /* Abaixo de 1280 px a barra divide o espaco com a busca, e so a tela
               atual fica — o caminho inteiro volta na largura que o comporta. */
            <li
              key={`${String(indice)}-${parte}`}
              className={`flex min-w-0 items-center gap-2 ${ultima ? '' : 'max-[1280px]:hidden'}`}
            >
              {indice > 0 ? (
                <span aria-hidden="true" className="txt-apoio text-tinta-apagada max-[1280px]:hidden">
                  ›
                </span>
              ) : null}
              <span
                {...(ultima ? { 'aria-current': 'page' as const } : {})}
                className={`txt-apoio truncate ${ultima ? 'font-semibold text-tinta' : 'text-tinta-secundaria'}`}
              >
                {parte}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* O item do menu (p.34): 40 px, raio 12, rotulo 14/500 no texto de lista e
   icone de 18 px. No trilho de icones, so o icone, ao centro. */
export function Navegacao({
  gavetaAberta = false,
  aoFecharGaveta,
}: {
  /** Abaixo de 1024 px o menu e gaveta, aberta pelo botao ☰ da barra superior (R01). */
  readonly gavetaAberta?: boolean;
  readonly aoFecharGaveta?: () => void;
} = {}) {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  /* PERMISSAO, e nao perfil: o menu mostra o que a pessoa consegue abrir, pela
     mesma lista que o portao do servidor confere. O recorte inteiro mora em
     `modulosDoMenu`/`secoesQueAbre`, e o componente do desenho o recebe
     pronto — a navegacao mudou de FORMA em 26/10/2026, e nao de regra. */
  const sessao = useSessaoEmMemoria();
  /* RECORTADO quer dizer "a sessao ja chegou", e nao "tem permissao": com a
     sessao em transito o menu mostra o mapa inteiro para nao piscar vazio. */
  const recortado = sessao?.permissoes !== undefined && sessao.permissoes !== null;
  /* O modulo atual sai da secao que casa por mais tempo (`localDoCaminho`):
     `/combustivel/lancamento` continua sendo Combustivel, e `/inspecao` e
     Ordens de servico, onde a tela mora no menu. */
  const moduloAtual = localDoCaminho(caminho)?.modulo.id;

  return (
    <NavegacaoLateral
      modulos={MODULOS}
      moduloAtual={moduloAtual}
      recortado={recortado}
      gavetaAberta={gavetaAberta}
      {...(aoFecharGaveta ? { aoFecharGaveta } : {})}
    />
  );
}



